import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Menu, Button, Tooltip } from 'antd'
import { ControlOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { httpGet, httpPut } from '../../common/http.service'
import { portalAccentGhostButtonStyle, useTheme, useThemeColors } from '../../common/ThemeContext'
import { Colors } from '../../common/constant'
import {
	WelcomeCustomizeDrawer,
	WelcomeModuleIcon,
	type WelcomeLayout,
	resolveIconComponent,
} from './WelcomeCustomizeDrawer'
import {
	MODULE_DEFS,
	MODULE_ROUTES,
	pathToModuleKey,
	sidebarModulePathMatches,
	readStoredWelcomeLayout,
	persistWelcomeLayout,
	readStoredWelcomeModuleIcons,
	persistWelcomeModuleIcons,
	WELCOME_LAYOUT_CHANGE_EVENT,
	getAppSettingsPayload,
	broadcastWelcomeLayout,
	mergeWelcomeLayoutFromServer,
} from './welcomeModules'
import {
	WELCOME_SIDEBAR_COLLAPSED_W,
	WELCOME_SIDEBAR_EXPANDED_W,
	useWelcomeSidebarSlotSafe,
} from '../../common/layout/WelcomeSidebarSlotContext'

/** Must stay in sync with `GlobalHeader` heights / colors */
function chromeHeaderMetrics(theme: 'edc' | 'primersoft' | 'dark') {
	if (theme === 'edc') {
		return { height: 64, topBarBg: '#254a89' as const }
	}
	if (theme === 'dark') {
		return { height: 64, topBarBg: '#0f172a' as const }
	}
	return { height: 56, topBarBg: '#414042' as const }
}

type Props = { children: React.ReactNode }

export default function WelcomeSidebarChrome({ children }: Props) {
	const navigate = useNavigate()
	const location = useLocation()
	const { theme, isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary } = themeColors
	const customizeBtnStyle = portalAccentGhostButtonStyle(theme, primary)
	const { height: headerHeight, topBarBg } = chromeHeaderMetrics(theme)
	const accentColor = theme === 'primersoft' ? primary : theme === 'dark' ? '#38bdf8' : Colors.Primary

	const [layoutMode, setLayoutMode] = useState<WelcomeLayout>(readStoredWelcomeLayout)
	const [moduleIcons, setModuleIcons] = useState<Record<string, WelcomeModuleIcon>>(readStoredWelcomeModuleIcons)
	const [rawSettings, setRawSettings] = useState<Record<string, unknown>>({})
	const [customizeOpen, setCustomizeOpen] = useState(false)
	const [collapsed, setCollapsed] = useState(false)
	const [hovered, setHovered] = useState(false)

	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const menuToggleRef = useRef<HTMLButtonElement | null>(null)

	const expanded = !collapsed || hovered
	const showSidebar = layoutMode === 'sidebar' && sidebarModulePathMatches(location.pathname)
	const sidebarWidth = expanded ? WELCOME_SIDEBAR_EXPANDED_W : WELCOME_SIDEBAR_COLLAPSED_W

	const slotCtx = useWelcomeSidebarSlotSafe()

	/** Drive Home offset from the real Menu control width (fixed button + label). */
	useLayoutEffect(() => {
		if (!slotCtx) return
		if (!showSidebar) {
			slotCtx.setSlotWidthPx(0)
			return
		}

		let ro: ResizeObserver | undefined
		let cancelled = false
		let raf1 = 0
		let raf2 = 0
		const gapPx = 4

		const measureAndBind = () => {
			const el = menuToggleRef.current
			if (!el) {
				slotCtx.setSlotWidthPx(96)
				return
			}
			const apply = () => {
				if (cancelled) return
				slotCtx.setSlotWidthPx(Math.ceil(el.getBoundingClientRect().width) + gapPx)
			}
			apply()
			ro = new ResizeObserver(apply)
			ro.observe(el)
		}

		raf1 = requestAnimationFrame(() => {
			if (cancelled) return
			raf2 = requestAnimationFrame(() => {
				if (cancelled) return
				measureAndBind()
			})
		})

		return () => {
			cancelled = true
			cancelAnimationFrame(raf1)
			cancelAnimationFrame(raf2)
			ro?.disconnect()
		}
	}, [slotCtx, showSidebar, expanded, collapsed, hovered, theme])

	const isWelcomePage =
		location.pathname === '/welcome' || location.pathname === '/welcome/'

	// Customize FAB + drawer are welcome-only; close drawer when navigating away.
	useEffect(() => {
		if (!isWelcomePage) setCustomizeOpen(false)
	}, [isWelcomePage])

	// Listen for layout change events from Welcome page
	useEffect(() => {
		const onLayout = (e: Event) => {
			const ce = e as CustomEvent<WelcomeLayout>
			if (ce.detail === 'sidebar' || ce.detail === 'tiles') setLayoutMode(ce.detail)
		}
		window.addEventListener(WELCOME_LAYOUT_CHANGE_EVENT, onLayout as EventListener)
		return () => window.removeEventListener(WELCOME_LAYOUT_CHANGE_EVENT, onLayout as EventListener)
	}, [])

	useEffect(() => {
		persistWelcomeModuleIcons(moduleIcons)
	}, [moduleIcons])

	useEffect(() => {
		httpGet('/api/v1/admin/app-settings')
			.then((res: unknown) => {
				const data = getAppSettingsPayload(res)
				const next = mergeWelcomeLayoutFromServer(data.welcomeLayout)
				setLayoutMode(next)
				persistWelcomeLayout(next)
				const nextIcons = (data.welcomeModuleIcons as Record<string, WelcomeModuleIcon>) ?? {}
				setModuleIcons(nextIcons)
				persistWelcomeModuleIcons(nextIcons)
				setRawSettings(data)
			})
			.catch(() => {})
	}, [])

	const saveLayout = useCallback(
		async (next: WelcomeLayout) => {
			setLayoutMode(next)
			persistWelcomeLayout(next)
			broadcastWelcomeLayout(next)
			try {
				await httpPut('/api/v1/admin/app-settings', {
					...rawSettings,
					welcomeLayout: next,
				})
				setRawSettings((prev) => ({ ...prev, welcomeLayout: next }))
				if (next === 'tiles') {
					navigate('/welcome', { replace: true })
				}
			} catch {
				/* non-fatal */
			}
		},
		[navigate, rawSettings],
	)

	if (!showSidebar) {
		return <>{children}</>
	}

	const selectedKey = pathToModuleKey(location.pathname)

	const menuItems = MODULE_DEFS.map((mod) => {
		const override = moduleIcons[mod.key]
		const SidebarIcon =
			override?.type === 'icon' && override.value
				? (resolveIconComponent(override.value) ?? mod.defaultIcon)
				: mod.defaultIcon
		return {
			key: mod.key,
			icon: (
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 28,
						height: 28,
						borderRadius: 7,
						background: selectedKey === mod.key ? `${accentColor}15` : 'transparent',
						transition: 'background 0.2s',
						flexShrink: 0,
					}}
				>
					<SidebarIcon
						style={{
							fontSize: 15,
							color: selectedKey === mod.key ? accentColor : themeColors.textSecondary,
							transition: 'color 0.2s',
						}}
					/>
				</span>
			),
			label: (
				<span
					style={{
						fontSize: 13.5,
						fontWeight: selectedKey === mod.key ? 600 : 450,
						color: selectedKey === mod.key ? accentColor : themeColors.text,
						letterSpacing: '-0.01em',
						transition: 'color 0.2s, font-weight 0.2s',
					}}
				>
					{mod.defaultLabel}
				</span>
			),
		}
	})

	const headerPad = theme === 'edc' ? 20 : 24

	return (
		<div style={{ position: 'relative', minHeight: `calc(100vh - ${headerHeight}px)` }}>
			{/* Under GlobalHeader (z1000): fills rail so no seam; does not capture clicks */}
			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: sidebarWidth,
					height: headerHeight,
					zIndex: 999,
					pointerEvents: 'none',
					background: topBarBg,
					borderBottom:
						theme === 'dark'
							? '1px solid #1e293b'
							: theme === 'edc'
								? 'none'
								: `1px solid ${themeColors.border}`,
					boxSizing: 'border-box',
				}}
				aria-hidden
			/>
			{/* Menu only above header — Home spacer can be small and actually visible */}
			<Button
				ref={menuToggleRef}
				id='wsb-menu-toggle'
				type='text'
				size='small'
				icon={
					collapsed ? (
						<MenuUnfoldOutlined style={{ fontSize: 16 }} />
					) : (
						<MenuFoldOutlined style={{ fontSize: 16 }} />
					)
				}
				onClick={() => {
					setCollapsed(!collapsed)
					setHovered(false)
				}}
				style={{
					position: 'fixed',
					top: headerHeight / 2,
					left: headerPad,
					zIndex: 1002,
					transform: 'translateY(-50%)',
					height: 36,
					padding: expanded ? '0 10px' : '0 8px',
					color: theme === 'edc' || theme === 'primersoft' ? '#ffffff' : themeColors.text,
					fontWeight: 500,
					fontSize: 14,
					display: 'inline-flex',
					alignItems: 'center',
				}}
			>
				{expanded ? 'Menu' : null}
			</Button>

			{/* Rail below header */}
			<div
				onMouseEnter={() => {
					if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
					if (collapsed) setHovered(true)
				}}
				onMouseLeave={() => {
					if (collapsed) {
						hoverTimerRef.current = setTimeout(() => setHovered(false), 140)
					}
				}}
				style={{
					position: 'fixed',
					top: headerHeight,
					left: 0,
					width: sidebarWidth,
					height: `calc(100vh - ${headerHeight}px)`,
					background: themeColors.bg,
					borderRight: `1px solid ${themeColors.border}`,
					boxShadow: expanded && collapsed
						? isDarkMode ? '4px 0 24px rgba(0,0,0,0.40)' : '4px 0 24px rgba(0,0,0,0.10)'
						: `1px 0 0 ${themeColors.border}`,
					zIndex: 900,
					display: 'flex',
					flexDirection: 'column',
					transition: 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s',
					overflow: 'hidden',
				}}
			>
				<div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
					<Menu
						className='wsb-menu'
						mode='inline'
						inlineCollapsed={!expanded}
						selectedKeys={selectedKey ? [selectedKey] : []}
						items={menuItems}
						onClick={({ key }) => {
							const path = MODULE_ROUTES[key]
							if (path) navigate(path)
						}}
						style={{
							border: 'none',
							padding: '8px 8px',
							background: 'transparent',
						}}
					/>
				</div>
			</div>

			{/* Page content — always full width, sidebar floats above */}
			<div
				style={{
					marginLeft: sidebarWidth,
					minHeight: `calc(100vh - ${headerHeight}px)`,
					transition: 'margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
				}}
			>
				{children}
			</div>

			{/* Menu item styles */}
			<style>{`
				.wsb-menu.ant-menu-inline, .wsb-menu.ant-menu-inline-collapsed {
					background: transparent !important;
				}
				.wsb-menu .ant-menu-item {
					height: 42px !important;
					line-height: 42px !important;
					border-radius: 8px !important;
					margin: 2px 0 !important;
					padding-left: 10px !important;
					display: flex;
					align-items: center;
					gap: 10px;
				}
				.wsb-menu .ant-menu-item:hover {
					background: ${isDarkMode ? '#ffffff08' : '#f8fafc'} !important;
				}
				.wsb-menu .ant-menu-item-selected {
					background: transparent !important;
				}
				.wsb-menu .ant-menu-item::after {
					display: none !important;
				}
				.wsb-menu.ant-menu-inline-collapsed .ant-menu-item {
					padding: 0 !important;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.wsb-menu.ant-menu .ant-menu-item .ant-menu-title-content {
					color: ${themeColors.text} !important;
				}
				.wsb-menu.ant-menu .ant-menu-item-selected .ant-menu-title-content {
					color: ${accentColor} !important;
				}
			`}</style>

			{isWelcomePage && (
				<>
					<Tooltip title='Layout Settings' placement='left'>
						<Button
							shape='round'
							icon={<ControlOutlined />}
							onClick={() => setCustomizeOpen(true)}
							style={{
								...customizeBtnStyle,
								position: 'fixed',
								bottom: 32,
								right: 32,
								zIndex: 1000,
								boxShadow: isDarkMode
									? '0 4px 16px rgba(0,0,0,0.35)'
									: '0 4px 16px rgba(0,0,0,0.12)',
								display: 'flex',
								alignItems: 'center',
								gap: 6,
								paddingLeft: 16,
								paddingRight: 16,
								height: 42,
								fontSize: 14,
							}}
						>
							Customize
						</Button>
					</Tooltip>

					<WelcomeCustomizeDrawer
						open={customizeOpen}
						onClose={() => setCustomizeOpen(false)}
						modules={MODULE_DEFS}
						moduleIcons={moduleIcons}
						onIconsChange={setModuleIcons}
						layout={layoutMode}
						onLayoutChange={saveLayout}
					/>
				</>
			)}
		</div>
	)
}
